import { AxiosResponse } from 'axios'
import ApiService from '@/plugins/api-plugin/services/api.service'
import { LinkedAccountsDao } from '@/interfaces/user-management/Accounts.dao'
import { OperationsCentreDao, PermissionAreaDao } from '@/interfaces/user-management/Catalogues.dao'
import { HistoryQuery, UserChangesPageDao } from '@/interfaces/user-management/Changes.dao'
import {
    CreateGroupBody,
    GroupPageDao,
    GroupRemovalResultDao,
    GroupRowDao,
    GroupWriteResultDao,
    UpdateGroupBody,
} from '@/interfaces/user-management/Groups.dao'
import { UserOverviewDao } from '@/interfaces/user-management/Overview.dao'
import {
    AccountFix,
    ApiPasswordBody,
    ApproveUserBody,
    BulkStatusBody,
    BulkStatusResultDao,
    BulkSuspendBody,
    CreateApiAccountBody,
    CreateUserBody,
    UpdateUserBody,
    UserExportBody,
    UserListPageDao,
    UserListQuery,
    UserPageDao,
    UserPasswordResultDao,
    UserRemovalResultDao,
    UserWriteResultDao,
} from '@/interfaces/user-management/Users.dao'

const BASE = '/user-management'
const WRITE = { suppressErrorAlert: true }

/**
 * The User Management pages (`/user-management`, role `Web_Nav_Admin_usermgt`). Writes skip the
 * global alert: the dialog that made the call shows the error.
 */
export default class UserManagementService extends ApiService {
    /** GET overview - KPIs, ranked attention checks, weekly sign-ins, recent sign-ins and email domains. */
    async overview(callerName: string): Promise<UserOverviewDao> {
        return this.request<undefined, UserOverviewDao>(
            'GET',
            `${BASE}/overview`,
            undefined,
            undefined,
            undefined,
            callerName,
        )
    }

    /** GET users - one page of the list with the tab, kind and quick filter counts; the previous read is cancelled. */
    async users(query: UserListQuery, callerName: string): Promise<UserListPageDao> {
        this.cancelRequest([callerName])
        return this.request<undefined, UserListPageDao>(
            'GET',
            `${BASE}/users`,
            { ...query },
            undefined,
            undefined,
            callerName,
        )
    }

    /** POST users/export - the CSV of the filters or the chosen ids; errors arrive as a Blob. */
    async exportUsers(body: UserExportBody, callerName: string): Promise<AxiosResponse<Blob>> {
        return this.request<UserExportBody, AxiosResponse<Blob>>(
            'POST',
            `${BASE}/users/export`,
            undefined,
            body,
            'blob',
            callerName,
            WRITE,
        )
    }

    /** GET users/:id - the user page header; a 404 is left to the page. */
    async user(id: number, callerName: string): Promise<UserPageDao> {
        return this.request<undefined, UserPageDao>(
            'GET',
            `${BASE}/users/${id}`,
            undefined,
            undefined,
            undefined,
            callerName,
            {
                suppressErrorAlert: true,
            },
        )
    }

    /** POST users - a person through the existing create flow, with the welcome email. */
    async createUser(body: CreateUserBody, callerName: string): Promise<UserWriteResultDao> {
        return this.request<CreateUserBody, UserWriteResultDao>(
            'POST',
            `${BASE}/users`,
            undefined,
            body,
            undefined,
            callerName,
            WRITE,
        )
    }

    /** POST api-accounts - an API account in CO only; the password comes back once. */
    async createApiAccount(body: CreateApiAccountBody, callerName: string): Promise<UserPasswordResultDao> {
        return this.request<CreateApiAccountBody, UserPasswordResultDao>(
            'POST',
            `${BASE}/api-accounts`,
            undefined,
            body,
            undefined,
            callerName,
            WRITE,
        )
    }

    /** POST users/suspend - bulk Suspend of up to 50 ids: idle 90+ days, recent ones when asked. */
    async suspendUsers(body: BulkSuspendBody, callerName: string): Promise<BulkStatusResultDao> {
        return this.request<BulkSuspendBody, BulkStatusResultDao>(
            'POST',
            `${BASE}/users/suspend`,
            undefined,
            body,
            undefined,
            callerName,
            WRITE,
        )
    }

    /** POST users/activate - bulk Activate of up to 50 suspended ids. */
    async activateUsers(body: BulkStatusBody, callerName: string): Promise<BulkStatusResultDao> {
        return this.request<BulkStatusBody, BulkStatusResultDao>(
            'POST',
            `${BASE}/users/activate`,
            undefined,
            body,
            undefined,
            callerName,
            WRITE,
        )
    }

    /** PATCH users/:id - only the fields sent change; a list replaces the current one. */
    async updateUser(id: number, body: UpdateUserBody, callerName: string): Promise<UserWriteResultDao> {
        return this.request<UpdateUserBody, UserWriteResultDao>(
            'PATCH',
            `${BASE}/users/${id}`,
            undefined,
            body,
            undefined,
            callerName,
            WRITE,
        )
    }

    /** DELETE users/:id - 409 when the account owns records or is an API account. */
    async deleteUser(id: number, callerName: string): Promise<UserRemovalResultDao> {
        return this.request<undefined, UserRemovalResultDao>(
            'DELETE',
            `${BASE}/users/${id}`,
            undefined,
            undefined,
            undefined,
            callerName,
            WRITE,
        )
    }

    /** POST users/:id/approve - lets a waiting user in with the groups and centres chosen. */
    async approveUser(id: number, body: ApproveUserBody, callerName: string): Promise<UserWriteResultDao> {
        return this.request<ApproveUserBody, UserWriteResultDao>(
            'POST',
            `${BASE}/users/${id}/approve`,
            undefined,
            body,
            undefined,
            callerName,
            WRITE,
        )
    }

    /** POST users/:id/reject - removes a sign-up request and its Auth0 identity; no email. */
    async rejectUser(id: number, callerName: string): Promise<UserRemovalResultDao> {
        return this.request<undefined, UserRemovalResultDao>(
            'POST',
            `${BASE}/users/${id}/reject`,
            undefined,
            undefined,
            undefined,
            callerName,
            WRITE,
        )
    }

    /** POST users/:id/suspend - CO first, then a person's Auth0 user is blocked; an API account is CO only. */
    async suspendUser(id: number, callerName: string): Promise<UserWriteResultDao> {
        return this.request<undefined, UserWriteResultDao>(
            'POST',
            `${BASE}/users/${id}/suspend`,
            undefined,
            undefined,
            undefined,
            callerName,
            WRITE,
        )
    }

    /** POST users/:id/activate - CO first, then a person's Auth0 user is unblocked; an API account is CO only. */
    async activateUser(id: number, callerName: string): Promise<UserWriteResultDao> {
        return this.request<undefined, UserWriteResultDao>(
            'POST',
            `${BASE}/users/${id}/activate`,
            undefined,
            undefined,
            undefined,
            callerName,
            WRITE,
        )
    }

    /** POST users/:id/api-password - a new password for an API account, typed or generated, returned once. */
    async newApiPassword(id: number, body: ApiPasswordBody, callerName: string): Promise<UserPasswordResultDao> {
        return this.request<ApiPasswordBody, UserPasswordResultDao>(
            'POST',
            `${BASE}/users/${id}/api-password`,
            undefined,
            body,
            undefined,
            callerName,
            WRITE,
        )
    }

    /** GET users/:id/linked-accounts - a live read of Auth0 and COBI. */
    async linkedAccounts(id: number, callerName: string): Promise<LinkedAccountsDao> {
        return this.request<undefined, LinkedAccountsDao>(
            'GET',
            `${BASE}/users/${id}/linked-accounts`,
            undefined,
            undefined,
            undefined,
            callerName,
        )
    }

    /** POST users/:id/linked-accounts/fix - runs one offered fix; 409 when the problem is already gone. */
    async fixLinkedAccount(id: number, fix: AccountFix, callerName: string): Promise<LinkedAccountsDao> {
        return this.request<{ fix: AccountFix }, LinkedAccountsDao>(
            'POST',
            `${BASE}/users/${id}/linked-accounts/fix`,
            undefined,
            { fix },
            undefined,
            callerName,
            WRITE,
        )
    }

    /** GET changes - every User Management write, newest first. */
    async changes(query: HistoryQuery, callerName: string): Promise<UserChangesPageDao> {
        return this.request<undefined, UserChangesPageDao>(
            'GET',
            `${BASE}/changes`,
            { ...query },
            undefined,
            undefined,
            callerName,
        )
    }

    /** GET users/:id/changes - the writes on one user, group writes that added or removed them included. */
    async userChanges(id: number, query: HistoryQuery, callerName: string): Promise<UserChangesPageDao> {
        return this.request<undefined, UserChangesPageDao>(
            'GET',
            `${BASE}/users/${id}/changes`,
            { ...query },
            undefined,
            undefined,
            callerName,
        )
    }

    /** GET groups/:id/changes - the writes on one group. */
    async groupChanges(id: number, query: HistoryQuery, callerName: string): Promise<UserChangesPageDao> {
        return this.request<undefined, UserChangesPageDao>(
            'GET',
            `${BASE}/groups/${id}/changes`,
            { ...query },
            undefined,
            undefined,
            callerName,
        )
    }

    /** GET groups - every group, most active first. */
    async groups(callerName: string): Promise<GroupRowDao[]> {
        return this.request<undefined, GroupRowDao[]>(
            'GET',
            `${BASE}/groups`,
            undefined,
            undefined,
            undefined,
            callerName,
        )
    }

    /** GET groups/:id - the group with its members; a 404 is left to the page. */
    async group(id: number, callerName: string): Promise<GroupPageDao> {
        return this.request<undefined, GroupPageDao>(
            'GET',
            `${BASE}/groups/${id}`,
            undefined,
            undefined,
            undefined,
            callerName,
            {
                suppressErrorAlert: true,
            },
        )
    }

    /** POST groups - a group with no members, optionally with the permissions of another. */
    async createGroup(body: CreateGroupBody, callerName: string): Promise<GroupWriteResultDao> {
        return this.request<CreateGroupBody, GroupWriteResultDao>(
            'POST',
            `${BASE}/groups`,
            undefined,
            body,
            undefined,
            callerName,
            WRITE,
        )
    }

    /** PATCH groups/:id - name, description, the permission set, members added and removed. */
    async updateGroup(id: number, body: UpdateGroupBody, callerName: string): Promise<GroupWriteResultDao> {
        return this.request<UpdateGroupBody, GroupWriteResultDao>(
            'PATCH',
            `${BASE}/groups/${id}`,
            undefined,
            body,
            undefined,
            callerName,
            WRITE,
        )
    }

    /** DELETE groups/:id - 409 while an active user is a member. */
    async deleteGroup(id: number, callerName: string): Promise<GroupRemovalResultDao> {
        return this.request<undefined, GroupRemovalResultDao>(
            'DELETE',
            `${BASE}/groups/${id}`,
            undefined,
            undefined,
            undefined,
            callerName,
            WRITE,
        )
    }

    /** GET permissions - the CODB roles by menu area. */
    async permissions(callerName: string): Promise<PermissionAreaDao[]> {
        return this.request<undefined, PermissionAreaDao[]>(
            'GET',
            `${BASE}/permissions`,
            undefined,
            undefined,
            undefined,
            callerName,
        )
    }

    /** GET operations-centres - every centre, sorted by name. */
    async operationsCentres(callerName: string): Promise<OperationsCentreDao[]> {
        return this.request<undefined, OperationsCentreDao[]>(
            'GET',
            `${BASE}/operations-centres`,
            undefined,
            undefined,
            undefined,
            callerName,
        )
    }
}
